// ============================================
// 2026-10-17（标绘层 S3）：GeoJSON 导入导出（数据互通，纯函数，可单测）
//  - 坐标映射：世界 (x, z) → GeoJSON [x, z]（y 不入坐标；导入 y 固定 0）
//  - properties 保留 kind/name/style/keyPoints（keyPoints 用于本工具内精确往返：箭头等生成类图元）
//  - 接受 FeatureCollection / Feature / 裸 geometry；Multi* 自动拆分
// 红线：GeoJSON 仅数据互通（无文件包/可执行内容），不做第三方标绘包本地导入入口。
// ============================================
import type { P3, AnnotationKind } from './primitives';
import { ANNOTATION_KINDS, isAreaKind, outlineFor } from './primitives';

export interface GeoJsonFeature {
  type: 'Feature';
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

export interface GeoJsonFeatureCollection {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

export interface GeoAnnotationLike {
  kind: string;
  name?: string;
  points: P3[];
  style?: Record<string, unknown>;
}

export interface ImportedAnnotation {
  kind: AnnotationKind;
  name: string;
  points: P3[];
  style?: Record<string, unknown>;
}

function isKnownKind(k: unknown): k is AnnotationKind {
  return typeof k === 'string' && (ANNOTATION_KINDS as readonly string[]).includes(k);
}

function keyPointsOf(value: unknown): P3[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const out: P3[] = [];
  for (const p of value) {
    if (!Array.isArray(p) || p.length < 2) return null;
    const x = Number((p as unknown[])[0]);
    const y = Number((p as unknown[])[1] ?? 0);
    const z = Number((p as unknown[])[2] ?? 0);
    if (![x, y, z].every(Number.isFinite)) return null;
    out.push([x, y, z]);
  }
  return out;
}

function coordsToP3(coords: unknown): P3 | null {
  if (!Array.isArray(coords)) return null;
  const x = Number(coords[0]);
  const z = Number(coords[1]);
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  return [x, 0, z];
}

function ringToPoints(ring: unknown): P3[] | null {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const pts: P3[] = [];
  for (const c of ring) {
    const p = coordsToP3(c);
    if (!p) return null;
    pts.push(p);
  }
  // 去掉闭合重复点（GeoJSON 线性环首尾相同）
  if (pts.length > 1) {
    const a = pts[0];
    const b = pts[pts.length - 1];
    if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[2] - b[2]) < 1e-9) pts.pop();
  }
  return pts.length >= 3 ? pts : null;
}

/** 标绘 → GeoJSON FeatureCollection */
export function annotationsToGeoJson(list: GeoAnnotationLike[]): GeoJsonFeatureCollection {
  const features: GeoJsonFeature[] = [];
  for (const a of list) {
    const outline = outlineFor(a.kind, a.points);
    let geometry: GeoJsonFeature['geometry'];
    if (a.kind === 'point' || a.kind === 'text') {
      const p = a.points[0] ?? [0, 0, 0];
      geometry = { type: 'Point', coordinates: [p[0], p[2]] };
    } else if (isAreaKind(a.kind)) {
      const ring = outline.map((p) => [p[0], p[2]]);
      if (ring.length >= 3) {
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (Math.abs(first[0] - last[0]) > 1e-9 || Math.abs(first[1] - last[1]) > 1e-9) ring.push([first[0], first[1]]);
        geometry = { type: 'Polygon', coordinates: [ring] };
      } else {
        geometry = { type: 'LineString', coordinates: ring };
      }
    } else {
      geometry = { type: 'LineString', coordinates: outline.map((p) => [p[0], p[2]]) };
    }
    features.push({
      type: 'Feature',
      geometry,
      properties: {
        kind: a.kind,
        name: a.name ?? '',
        ...(a.style ? { style: a.style } : {}),
        keyPoints: a.points.map((p) => [p[0], p[1], p[2]]),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

function featureToAnnotations(f: GeoJsonFeature, fallbackIndex: number): ImportedAnnotation[] {
  if (!f || f.type !== 'Feature' || !f.geometry || typeof f.geometry !== 'object') return [];
  const geoType = String(f.geometry.type || '');
  const props = (f.properties || {}) as Record<string, unknown>;
  const propKind = isKnownKind(props.kind) ? props.kind : null;
  const name = String(props.name || '') || `导入标绘 ${fallbackIndex + 1}`;
  const style = props.style && typeof props.style === 'object' ? (props.style as Record<string, unknown>) : undefined;
  const keyPoints = keyPointsOf(props.keyPoints);
  // keyPoints + 已知 kind：本工具精确往返
  if (propKind && keyPoints && keyPoints.length > 0) {
    return [{ kind: propKind, name, points: keyPoints, style }];
  }
  const coords = f.geometry.coordinates;
  const out: ImportedAnnotation[] = [];
  switch (geoType) {
    case 'Point': {
      const p = coordsToP3(coords);
      if (!p) return [];
      const kind: AnnotationKind = propKind === 'text' ? 'text' : propKind && !isAreaKind(propKind) && propKind !== 'polyline' ? propKind : 'point';
      out.push({ kind, name, points: [p], style });
      return out;
    }
    case 'MultiPoint': {
      if (!Array.isArray(coords)) return [];
      coords.forEach((c, i) => {
        const p = coordsToP3(c);
        if (p) out.push({ kind: 'point', name: `${name} ${i + 1}`, points: [p], style });
      });
      return out;
    }
    case 'LineString': {
      if (!Array.isArray(coords) || coords.length < 2) return [];
      const pts: P3[] = [];
      for (const c of coords) {
        const p = coordsToP3(c);
        if (!p) return [];
        pts.push(p);
      }
      const kind: AnnotationKind = propKind && !isAreaKind(propKind) && propKind !== 'point' && propKind !== 'text' ? propKind : 'polyline';
      out.push({ kind, name, points: pts, style });
      return out;
    }
    case 'MultiLineString': {
      if (!Array.isArray(coords)) return [];
      coords.forEach((ls, i) => {
        if (!Array.isArray(ls) || ls.length < 2) return;
        const pts: P3[] = [];
        for (const c of ls) {
          const p = coordsToP3(c);
          if (!p) return;
          pts.push(p);
        }
        out.push({ kind: 'polyline', name: `${name} ${i + 1}`, points: pts, style });
      });
      return out;
    }
    case 'Polygon': {
      const ring = Array.isArray(coords) ? coords[0] : null;
      const pts = ringToPoints(ring);
      if (!pts) return [];
      const kind: AnnotationKind = propKind && isAreaKind(propKind) && propKind !== 'arrowStraight' && propKind !== 'arrowFine' && propKind !== 'arrowCurve' ? propKind : 'polygon';
      out.push({ kind, name, points: pts, style });
      return out;
    }
    case 'MultiPolygon': {
      if (!Array.isArray(coords)) return [];
      coords.forEach((poly, i) => {
        const ring = Array.isArray(poly) ? poly[0] : null;
        const pts = ringToPoints(ring);
        if (pts) out.push({ kind: 'polygon', name: `${name} ${i + 1}`, points: pts, style });
      });
      return out;
    }
    default:
      return [];
  }
}

/** GeoJSON → 标绘（支持 FeatureCollection / Feature / 裸 geometry / 裸坐标数组） */
export function geoJsonToAnnotations(input: unknown): ImportedAnnotation[] {
  const src = typeof input === 'string' ? JSON.parse(input) : input;
  if (!src || typeof src !== 'object') throw new Error('GeoJSON 内容无效');
  const obj = src as Record<string, unknown>;
  const out: ImportedAnnotation[] = [];
  if (obj.type === 'FeatureCollection') {
    const features = Array.isArray(obj.features) ? obj.features : [];
    if (!features.length) throw new Error('GeoJSON 中无可导入的 Feature');
    features.forEach((f, i) => out.push(...featureToAnnotations(f as GeoJsonFeature, i)));
  } else if (obj.type === 'Feature') {
    out.push(...featureToAnnotations(obj as unknown as GeoJsonFeature, 0));
  } else if (typeof obj.type === 'string' && 'coordinates' in obj) {
    const geom = obj as Record<string, unknown>;
    const fake: GeoJsonFeature = {
      type: 'Feature',
      geometry: { type: String(geom.type), coordinates: geom.coordinates },
      properties: (geom.properties as Record<string, unknown>) || {},
    };
    out.push(...featureToAnnotations(fake, 0));
  } else {
    throw new Error('GeoJSON 类型不支持（需要 FeatureCollection / Feature / geometry）');
  }
  if (!out.length) throw new Error('GeoJSON 中无可导入的标绘（支持 Point/LineString/Polygon）');
  return out;
}
